import { calcularConflitos, overlaps } from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoIntervalo,
  CargaTrabalhoProfissional,
} from "@/modules/carga-trabalho";

import { calcularDataLimite, formatDataBr } from "./equipe-obra.rules";
import type {
  ConflitoNovaAlocacao,
  DivergenciasAlocacao,
  EquipeObraTarget,
  IndisponibilidadeNovaAlocacao,
  NovaAlocacao,
  PapelIncompletoItem,
} from "./equipe-obra.types";
import type { PapelObra } from "./papeis-obra.types";
import type { Profissional } from "./profissionais.types";

/**
 * Verificações da equipe da obra (HU 2059 e 2530), sem efeitos: a tela decide a ordem
 * (conflitos → indisponibilidade → equipe incompleta) e mostra os modais.
 */

/** Prefixo do id da nova alocação no Gantt e no cálculo de conflito. */
export const NOVA_ALOCACAO_PREFIX = "nova-";

/** A nova alocação no formato da carga de trabalho (obra sem etapas e não cancelada). */
export function novaAlocacaoNaCarga(
  nova: NovaAlocacao,
  obra: Pick<EquipeObraTarget, "id" | "codigo" | "nome">,
): CargaTrabalhoAlocacao {
  return {
    id: `${NOVA_ALOCACAO_PREFIX}${nova.id}`,
    papelObraId: nova.papelObraId,
    inicio: nova.inicio,
    fim: nova.fim,
    obra: {
      id: obra.id,
      codigo: obra.codigo,
      nome: obra.nome,
      situacao: "",
      embarcacao: null,
      etapas: { planejamento: null, obra: null, fechamento: null },
    },
  };
}

/**
 * Conflitos da equipe com a regra de profissionais (`calcularConflitos`): só contam
 * alocações de outras obras; obra cancelada não conta.
 */
export function detectarConflitos(
  novas: NovaAlocacao[],
  obra: Pick<EquipeObraTarget, "id" | "codigo" | "nome">,
  profissionais: CargaTrabalhoProfissional[],
): ConflitoNovaAlocacao[] {
  return novas.flatMap((nova) => {
    const profissional = profissionais.find((item) => item.id === nova.usuarioId);
    if (!profissional) return [];

    const alvo = novaAlocacaoNaCarga(nova, obra);
    const outrasObras = profissional.alocacoes.filter((item) => item.obra.id !== obra.id);

    return calcularConflitos([alvo, ...outrasObras])
      .filter((conflito) => conflito.a.id === alvo.id || conflito.b.id === alvo.id)
      .map((conflito) => ({
        usuarioId: profissional.id,
        profissional: profissional.nome,
        nova,
        existente: conflito.a.id === alvo.id ? conflito.b : conflito.a,
        inicio: conflito.inicio,
        fim: conflito.fim,
      }));
  });
}

/** Indisponibilidades de um profissional da equipe, reunidas das fontes disponíveis. */
export interface IndisponibilidadesDoProfissional {
  id: string;
  nome: string;
  periodos: CargaTrabalhoIntervalo[];
}

/**
 * Une as indisponibilidades da carga de trabalho (HU 1779) às da listagem de
 * profissionais (HU 1761), sem repetir o mesmo intervalo.
 */
export function unirIndisponibilidades(
  usuarioIds: string[],
  carga: CargaTrabalhoProfissional[],
  listagem: Profissional[],
): IndisponibilidadesDoProfissional[] {
  return [...new Set(usuarioIds)].map((id) => {
    const daCarga = carga.find((item) => item.id === id);
    const daListagem = listagem.find((item) => item.id === id);
    const periodos = new Map<string, CargaTrabalhoIntervalo>();
    for (const periodo of [
      ...(daCarga?.indisponibilidades ?? []),
      ...(daListagem?.indisponibilidade.periodos ?? []),
    ]) {
      const intervalo = { inicio: periodo.inicio.slice(0, 10), fim: periodo.fim.slice(0, 10) };
      if (intervalo.inicio && intervalo.fim) {
        periodos.set(`${intervalo.inicio}|${intervalo.fim}`, intervalo);
      }
    }
    return { id, nome: daCarga?.nome || daListagem?.nome || "", periodos: [...periodos.values()] };
  });
}

export function detectarIndisponibilidades(
  novas: NovaAlocacao[],
  profissionais: IndisponibilidadesDoProfissional[],
): IndisponibilidadeNovaAlocacao[] {
  return novas.flatMap((nova) => {
    const profissional = profissionais.find((item) => item.id === nova.usuarioId);
    if (!profissional) return [];
    return profissional.periodos
      .filter((periodo) => overlaps(nova, periodo))
      .map((indisponivel) => ({
        usuarioId: profissional.id,
        profissional: profissional.nome,
        nova,
        indisponivel,
      }));
  });
}

/** Tag de prazo no aviso de equipe incompleta: "Até D-30 · até 08/02/2030". */
export const rotuloLimiteIncompleta = (rotulo: string, data: string) =>
  `Até ${rotulo} · até ${data}`;

/** Situação de cada papel obrigatório para o aviso de equipe incompleta. */
export function resumirPapeisObrigatorios(
  obrigatorios: PapelObra[],
  rows: { papelObraId: string; usuarioId: string | null }[],
  profissionais: Pick<Profissional, "id" | "nome">[],
  obra: Pick<EquipeObraTarget, "inicio" | "fim">,
): PapelIncompletoItem[] {
  return obrigatorios.map((papel) => {
    const row = rows.find((item) => item.papelObraId === papel.id && item.usuarioId);
    const limite = calcularDataLimite(papel.janela, obra.inicio, obra.fim);

    return {
      id: papel.id,
      nome: papel.nome,
      limite: limite ? rotuloLimiteIncompleta(limite.rotulo, formatDataBr(limite.data)) : null,
      adicionado: Boolean(row),
      profissional: row
        ? (profissionais.find((item) => item.id === row.usuarioId)?.nome ?? null)
        : null,
    };
  });
}

/** Papéis obrigatórios ainda sem profissional (viram pendência no core). */
export function papeisObrigatoriosPendentes(resumo: PapelIncompletoItem[]): PapelIncompletoItem[] {
  return resumo.filter((papel) => !papel.adicionado);
}

/**
 * Divergências devolvidas pelo servidor (409 `DIVERGENCIAS_ALOCACAO`) no formato dos modais
 * do Salvar. O servidor só envia ids: o nome do profissional e do papel vêm da tela.
 */
export function divergenciasParaVerificacao(
  divergencias: DivergenciasAlocacao,
  novas: NovaAlocacao[],
  obra: Pick<EquipeObraTarget, "id" | "codigo" | "nome">,
  nomeProfissional: (usuarioId: string) => string,
  nomePapel: (papelObraId: string) => string | undefined,
): { conflitos: ConflitoNovaAlocacao[]; indisponiveis: IndisponibilidadeNovaAlocacao[] } {
  const nova = (
    usuarioId: string,
    papelObraId: string,
    periodo: CargaTrabalhoIntervalo,
  ): NovaAlocacao => {
    const daTela = novas.find(
      (item) => item.usuarioId === usuarioId && item.papelObraId === papelObraId,
    );
    return {
      id: daTela?.id ?? `${usuarioId}-${papelObraId}`,
      usuarioId,
      papelObraId,
      papel: daTela?.papel ?? nomePapel(papelObraId),
      inicio: periodo.inicio,
      fim: periodo.fim,
    };
  };

  return {
    conflitos: divergencias.conflitos.map((item) => ({
      usuarioId: item.usuarioId,
      profissional: nomeProfissional(item.usuarioId),
      nova: nova(item.usuarioId, item.papelObraId, item.novaAlocacao),
      existente: {
        id: `servidor-${item.obraAtual.id}`,
        papelObraId: "",
        inicio: item.obraAtual.inicio,
        fim: item.obraAtual.fim,
        obra: {
          id: item.obraAtual.id,
          codigo: "",
          nome: item.obraAtual.nome,
          situacao: "",
          embarcacao: null,
          etapas: { planejamento: null, obra: null, fechamento: null },
        },
      },
      inicio: item.coincidencia.inicio,
      fim: item.coincidencia.fim,
    })),
    indisponiveis: divergencias.indisponibilidades.map((item) => ({
      usuarioId: item.usuarioId,
      profissional: nomeProfissional(item.usuarioId),
      nova: nova(item.usuarioId, item.papelObraId, item.periodoObra),
      indisponivel: item.periodoIndisponivel,
    })),
  };
}
